import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";
import useSeo from "@/hooks/useSeo";
import SectionHeading from "@/components/feature/SectionHeading";
import Breadcrumb from "@/components/feature/Breadcrumb";

const SUBMIT_ADDR = "https://readdy.ai/api/form/da0ki003256a3o8kotsg";

const milestones = [
  { year: "2020", title: "漫天堂正式上线", desc: "从一个小型的漫画推荐博客起步，立志让好作品被更多人看见。" },
  { year: "2022", title: "人气榜上线", desc: "推出实时人气榜与热度排行，榜单机制广受读者好评。" },
  { year: "2024", title: "六大分类全面覆盖", desc: "热血、悬疑、治愈、恋爱、科幻、搞笑分类全部上线。" },
  { year: "2026", title: "成为漫迷信赖的榜单", desc: "累计收录作品 860+ 部，每日为数十万读者推荐好漫画。" },
];

const honors = [
  { icon: "ri-medal-fill", title: "权威榜单", desc: "以真实热度与口碑为准，榜单数据透明可信。" },
  { icon: "ri-emotion-happy-line", title: "读者信赖", desc: "数万读者持续追更，口碑推荐率常年领先。" },
  { icon: "ri-vip-crown-fill", title: "编辑严选", desc: "专业编辑逐部审核，宁缺毋滥，推荐不走弯路。" },
  { icon: "ri-verified-badge-fill", title: "内容专业", desc: "从评分到解读，内容均由团队精心打磨。" },
];

const values = [
  { icon: "ri-heart-3-line", title: "热爱", desc: "因为热爱漫画，所以认真做每一个榜单。" },
  { icon: "ri-eye-line", title: "真实", desc: "用真实数据说话，不掺水、不刷榜。" },
  { icon: "ri-lightbulb-flash-line", title: "洞察", desc: "帮你在海量内容里，快速找到心仪之作。" },
  { icon: "ri-community-line", title: "同行", desc: "与读者、创作者一起，共建漫画社区。" },
];

export default function AboutPage() {
  const [formError, setFormError] = useState("");
  const [success, setSuccess] = useState(false);

  useSeo({
    title: "关于漫天堂 | 漫画人气榜与热度排行平台介绍",
    description:
      "了解漫天堂：一个专注漫画人气榜与热度排行的内容平台，提供高分漫画推荐与必看清单。查看品牌故事、荣誉口碑与联系我们。",
    keywords:
      "漫天堂,关于漫天堂,漫天堂简介,漫天堂口碑,漫天堂联系方式,漫画推荐平台,漫画人气榜",
    canonicalPath: "/about",
    ogType: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://bestmanga.github.io/" },
          { "@type": "ListItem", position: 2, name: "关于我们", item: "https://bestmanga.github.io/about" },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "漫天堂",
        description: "聚合漫画人气榜与热度排行，实时更新编辑推荐与高分漫画的推荐平台。",
        url: "https://bestmanga.github.io/",
        email: "contact@mantiantang.com",
        foundingDate: "2020",
      },
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "关于漫天堂",
        url: "https://bestmanga.github.io/about",
        inLanguage: "zh-CN",
      },
    ],
  });

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const honeypot = (data.get("website_alt") as string)?.trim();
    if (honeypot) {
      setSuccess(true);
      setFormError("");
      form.reset();
      return;
    }
    data.delete("website_alt");
    setFormError("");
    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: "POST",
        body: data,
        headers: { Accept: "application/json" },
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } =
        {};
      try {
        parsed = responseText ? JSON.parse(responseText) : {};
      } catch {
        parsed = {};
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      if (res.ok && parsed?.code === "OK" && !serverMsg.includes("spam")) {
        setSuccess(true);
        form.reset();
      } else {
        setFormError(serverMsg || "提交失败，请稍后再试。");
      }
    } catch {
      setFormError("网络异常，请稍后再试。");
    }
  }

  return (
    <div>
      <section className="bg-primary-500 py-12 text-background-50 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <Breadcrumb items={[{ label: "关于我们" }]} />
          <div className="flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-background-50/15 text-xl backdrop-blur">
              <i className="ri-information-line"></i>
            </span>
            <div>
              <h1 className="font-heading text-2xl font-extrabold md:text-3xl">关于漫天堂</h1>
              <p className="mt-1 text-sm text-background-100/90">
                品牌故事 · 荣誉口碑 · 联系我们
              </p>
            </div>
          </div>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-background-100 md:text-base">
            漫天堂是一个专注漫画人气与热度排行的内容平台。我们聚合人气榜与热度排行，实时更新编辑推荐与高分漫画，用必看清单帮你快速锁定当前最值得追的作品。
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <SectionHeading
          eyebrow="品牌介绍"
          title="我们为什么做漫天堂"
          description="面对海量的漫画内容，选择常常让人疲惫。漫天堂想做的，就是替你把关、为你筛选，让每一次追漫都从容不迫。"
        />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="overflow-hidden rounded-lg">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20creative%20team%20workspace%20with%20manga%20comic%20art%20boards%20and%20digital%20tablets%2C%20soft%20golden%20light%2C%20cream%20and%20amber%20palette%2C%20editorial%20photography%20for%20a%20content%20brand%20team%20story&width=1200&height=760&seq=about-brand&orientation=landscape"
              alt="漫天堂编辑部工作场景"
              className="h-full w-full object-cover"
            />
          </div>
          <div className="flex flex-col justify-center gap-4">
            <p className="text-[15px] leading-8 text-foreground-800">
              漫天堂诞生于一群漫画爱好者的小小坚持：好作品不该被埋没，读者的时间不该被浪费。于是我们从一本本漫画出发，用真实的数据与真诚的推荐，搭建起今天这个人气与热度并重的榜单平台。
            </p>
            <p className="text-[15px] leading-8 text-foreground-800">
              六年来，我们从一个小博客成长为覆盖六大分类、收录 860+ 部作品的漫画榜单社区。无论你是刚入坑的新读者，还是阅片无数的老漫迷，都能在这里找到值得花时间的好作品。
            </p>
            <div className="mt-2 grid grid-cols-2 gap-3">
              {values.map((v) => (
                <div key={v.title} className="rounded-lg border border-background-200/70 bg-background-100 p-4">
                  <i className={`${v.icon} text-xl text-primary-600`}></i>
                  <h3 className="mt-2 text-sm font-bold text-foreground-950">{v.title}</h3>
                  <p className="mt-1 text-xs leading-relaxed text-foreground-600">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <SectionHeading eyebrow="发展历程" title="一步步走来" align="center" />
          <ol className="mx-auto grid max-w-4xl grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {milestones.map((m, i) => (
              <li key={m.year} className="relative rounded-lg border border-background-200/70 bg-background-50 p-5">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-bold text-accent-900">
                  <i className="ri-calendar-line"></i>
                  {m.year}
                </span>
                <h3 className="mt-3 text-sm font-bold text-foreground-950">{m.title}</h3>
                <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">{m.desc}</p>
                <span className="absolute -top-3 right-4 font-heading text-4xl font-black text-secondary-100">
                  0{i + 1}
                </span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <SectionHeading eyebrow="荣誉与口碑" title="被认可，也被信赖" description="口碑是漫天堂最好的名片，也是我们不断前行的动力。" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {honors.map((h) => (
            <div key={h.title} className="rounded-lg border border-background-200/70 bg-background-50 p-5 text-center">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-xl text-primary-600">
                <i className={h.icon}></i>
              </span>
              <h3 className="mt-3 text-sm font-bold text-foreground-950">{h.title}</h3>
              <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">{h.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-background-100 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
            <div>
              <SectionHeading
                eyebrow="联系我们"
                title="和我们聊聊"
                description="无论是推荐好作品、提出建议还是商务合作，欢迎通过表单或联系方式告诉我们。"
              />
              <ul className="grid gap-4">
                {[
                  { icon: "ri-mail-line", label: "邮箱", value: "contact@mantiantang.com" },
                  { icon: "ri-wechat-line", label: "微信公众号", value: "漫天堂官方" },
                  { icon: "ri-send-plane-line", label: "投稿 / 合作", value: "hi@mantiantang.com" },
                  { icon: "ri-time-line", label: "工作时间", value: "每日 09:00 - 22:00" },
                ].map((c) => (
                  <li key={c.label} className="flex items-center gap-4 rounded-lg border border-background-200/70 bg-background-50 p-4">
                    <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-lg text-accent-700">
                      <i className={c.icon}></i>
                    </span>
                    <div>
                      <p className="text-xs text-foreground-500">{c.label}</p>
                      <p className="text-sm font-semibold text-foreground-950">{c.value}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-6 flex flex-wrap gap-2">
                {categories.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/category/${c.slug}`}
                    className="rounded-full bg-background-50 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:bg-primary-500 hover:text-background-50"
                  >
                    {c.name}漫画
                  </Link>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-background-200/70 bg-background-50 p-6 md:p-8">
              <h2 className="text-lg font-bold text-foreground-950">发送消息给我们</h2>
              <p className="mt-2 text-sm text-foreground-600">
                你的每一条反馈，都会帮助我们做得更好。
              </p>
              <form
                data-readdy-form="about-contact"
                id="about-contact-form"
                onSubmit={handleSubmit}
                className="mt-6 grid grid-cols-1 gap-4"
                noValidate
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="contact-name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      你的称呼
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      required
                      placeholder="怎么称呼你"
                      className="w-full rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      邮箱
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      required
                      placeholder="you@example.com"
                      autoComplete="email"
                      className="w-full rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-subject" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    主题
                  </label>
                  <input
                    id="contact-subject"
                    type="text"
                    name="subject"
                    placeholder="你想聊点什么"
                    className="w-full rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    留言内容
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="请尽量详细描述，方便我们更快回复你（500 字以内）"
                    className="w-full resize-none rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                  ></textarea>
                  <p className="mt-1 text-right text-xs text-foreground-400">最多 500 字</p>
                </div>
                <input
                  type="text"
                  name="website_alt"
                  autoComplete="off"
                  tabIndex={-1}
                  aria-hidden="true"
                  readOnly
                  className="antispam-alt"
                />
                <button
                  type="submit"
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  提交留言
                  <i className="ri-send-plane-fill ml-2"></i>
                </button>
                {success && (
                  <p className="rounded-md bg-accent-100 px-4 py-3 text-sm font-medium text-accent-900">
                    已收到你的留言，我们会尽快回复，感谢支持！
                  </p>
                )}
                {formError && (
                  <p className="rounded-md bg-primary-100 px-4 py-3 text-sm font-medium text-primary-900">
                    {formError}
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}